import React, { useEffect, useRef } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { crudSelectStyles } from "@/components/tables/fields/crudSelectStyles";
import type { SelectOption } from "@/components/tables/fields/selectOptions";

const AUTO_FOCUS_RETRY_MS = 250;

export interface CrudSelectInputProps {
  name: string;
  id?: string;
  options: SelectOption[];
  value: SelectOption | SelectOption[] | undefined;
  isMulti?: boolean;
  loading: boolean;
  disabled: boolean;
  hasError: boolean;
  autoFocus?: boolean;
  isOptionDisabled: (option: SelectOption) => boolean;
  onChange: (selection: any) => void;
  onBlur: () => void;
}

const CrudSelectInput = ({
  name,
  id,
  options,
  value,
  isMulti,
  loading,
  disabled,
  hasError,
  autoFocus,
  isOptionDisabled,
  onChange,
  onBlur,
}: CrudSelectInputProps) => {
  const { t } = useTranslation();
  const selectRef = useRef<any>(null);

  useEffect(() => {
    if (!autoFocus) return;
    const focusInterval = setInterval(() => {
      if (selectRef.current) {
        selectRef.current.focus();
        clearInterval(focusInterval);
      }
    }, AUTO_FOCUS_RETRY_MS);
    return () => clearInterval(focusInterval);
  }, [autoFocus]);

  return (
    <Select
      name={name}
      onBlur={onBlur}
      ref={selectRef}
      isMulti={isMulti}
      value={value}
      options={options}
      isLoading={loading}
      isOptionDisabled={isOptionDisabled}
      isClearable={true}
      isSearchable={true}
      isDisabled={disabled}
      id={id}
      onChange={onChange}
      menuPortalTarget={document.body}
      placeholder={t("select_placeholder")}
      loadingMessage={() => t("loading")}
      noOptionsMessage={({ inputValue }) =>
        inputValue.trim().length > 0
          ? t("no_options_found")
          : t("type_to_search_placeholder")
      }
      styles={crudSelectStyles(hasError)}
    />
  );
};

export default CrudSelectInput;
